import type { ReactNode } from 'react';

/**
 * Peças de interface reutilizadas em todas as telas.
 *
 * Deliberadamente poucas e pequenas. Uma biblioteca de componentes grande demais
 * numa aplicação deste porte custa mais para manter do que economiza.
 */

/* ─────────────────────────────────────────────────────────── Cabeçalho ── */

export function CabecalhoDaPagina({
  titulo,
  descricao,
  acoes,
}: {
  titulo: string;
  descricao?: string | undefined;
  acoes?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4 pb-6">
      <div>
        <h1 className="text-[1.75rem] leading-tight text-[color:var(--texto)]">{titulo}</h1>
        {descricao && (
          <p className="mt-1 text-sm text-[color:var(--texto-suave)]">{descricao}</p>
        )}
      </div>
      {acoes && <div className="flex items-center gap-2">{acoes}</div>}
    </header>
  );
}

/* ────────────────────────────────────────────────────────────── Cartão ── */

export function Cartao({
  children,
  className = '',
  semPadding = false,
}: {
  children: ReactNode;
  className?: string;
  semPadding?: boolean;
}) {
  return (
    <section
      className={`rounded-md border bg-[color:var(--superficie-elevada)] ${
        semPadding ? '' : 'p-5'
      } ${className}`}
      style={{ boxShadow: 'var(--sombra-cartao)' }}
    >
      {children}
    </section>
  );
}

export function TituloDeSecao({
  children,
  auxiliar,
}: {
  children: ReactNode;
  auxiliar?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-baseline justify-between gap-4">
      <h2 className="text-lg text-[color:var(--texto)]">{children}</h2>
      {auxiliar && (
        <span className="text-xs text-[color:var(--texto-tenue)]">{auxiliar}</span>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────── Indicadores ── */

export type Tom = 'neutro' | 'acento' | 'recebido' | 'atencao' | 'vencido';

const COR_DO_TOM: Record<Tom, string> = {
  neutro: 'var(--texto)',
  acento: 'var(--acento)',
  recebido: 'var(--color-recebido)',
  atencao: 'var(--color-atencao)',
  vencido: 'var(--color-vencido)',
};

/**
 * Cartão de indicador.
 *
 * O valor é o herói: grande, e na cor do estado quando o estado importa. O rótulo
 * fica pequeno e em versalete acima; a nota de rodapé explica de onde vem o número —
 * sem ela, "R$ 41.362,85" não diz se é do mês, do ano ou de sempre.
 */
export function Indicador({
  rotulo,
  valor,
  nota,
  tom = 'neutro',
}: {
  rotulo: string;
  valor: string;
  nota?: string;
  tom?: Tom;
}) {
  return (
    <Cartao className="relative overflow-hidden">
      {/* A faixa de cor só aparece quando o indicador tem estado. Pintar todos os
          cartões de cores diferentes, como fazia a versão anterior, transforma a
          cor em decoração e ela deixa de significar qualquer coisa. */}
      {tom !== 'neutro' && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-[3px]"
          style={{ background: COR_DO_TOM[tom] }}
        />
      )}
      <p className="text-[0.6875rem] font-medium uppercase tracking-[0.1em] text-[color:var(--texto-tenue)]">
        {rotulo}
      </p>
      <p
        className="tabular mt-2 text-[1.75rem] leading-none font-semibold"
        style={{ color: COR_DO_TOM[tom] }}
      >
        {valor}
      </p>
      {nota && <p className="mt-2 text-xs text-[color:var(--texto-suave)]">{nota}</p>}
    </Cartao>
  );
}

/* ───────────────────────────────────────────────────────────── Fichas ── */

/** Etiqueta de estado. A cor vem do domínio, não da estética. */
export function Ficha({
  children,
  cor,
  tom = 'neutro',
}: {
  children: ReactNode;
  /** Cor explícita — usada pelas fases, que trazem a própria cor do servidor. */
  cor?: string;
  tom?: Tom;
}) {
  const corFinal = cor ?? COR_DO_TOM[tom];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap"
      style={{
        color: corFinal,
        // O fundo é a própria cor a 12% — assim a ficha acompanha qualquer paleta de
        // fase sem precisar de uma variante declarada para cada uma.
        background: `color-mix(in srgb, ${corFinal} 12%, transparent)`,
      }}
    >
      <span aria-hidden className="size-1.5 rounded-full" style={{ background: corFinal }} />
      {children}
    </span>
  );
}

/* ──────────────────────────────────────────────────────────── Botões ── */

export function Botao({
  children,
  onClick,
  tipo = 'secundario',
  submeter = false,
  desabilitado = false,
  className = '',
  formulario,
}: {
  children: ReactNode;
  onClick?: () => void;
  tipo?: 'primario' | 'secundario' | 'discreto';
  submeter?: boolean;
  desabilitado?: boolean;
  className?: string;
  /**
   * `id` do formulário que este botão submete, quando ele fica FORA do `<form>` —
   * caso do rodapé fixo de um modal. Sem isso, o botão de salvar não submeteria e
   * o Enter dentro dos campos deixaria de funcionar.
   */
  formulario?: string;
}) {
  const estilos: Record<string, string> = {
    primario:
      'bg-tinta-950 text-papel-50 hover:bg-tinta-800 disabled:opacity-45 dark:bg-ouro-700 dark:text-tinta-950 dark:hover:bg-ouro-600',
    secundario:
      'border bg-[color:var(--superficie-elevada)] text-[color:var(--texto)] hover:bg-[color:var(--superficie-afundada)] disabled:opacity-45',
    discreto:
      'text-[color:var(--texto-suave)] hover:text-[color:var(--acento)] disabled:opacity-45',
  };

  return (
    <button
      type={submeter ? 'submit' : 'button'}
      {...(formulario ? { form: formulario } : {})}
      onClick={onClick}
      disabled={desabilitado}
      className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed ${estilos[tipo]} ${className}`}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────── Estados vazios ── */

export function Carregando({ children = 'Carregando…' }: { children?: ReactNode }) {
  return (
    <div className="py-16 text-center text-sm text-[color:var(--texto-tenue)]">{children}</div>
  );
}

/**
 * Estado vazio.
 *
 * Diz o que aconteceu E o que fazer. "Nenhum resultado" sozinho deixa a pessoa
 * parada, sem saber se filtrou demais ou se de fato não há nada.
 */
export function Vazio({ titulo, saida }: { titulo: string; saida?: ReactNode }) {
  return (
    <div className="py-14 text-center">
      <p className="text-sm text-[color:var(--texto-suave)]">{titulo}</p>
      {saida && <div className="mt-3">{saida}</div>}
    </div>
  );
}

export function Alerta({
  children,
  tom = 'atencao',
}: {
  children: ReactNode;
  tom?: Exclude<Tom, 'neutro'>;
}) {
  const fundo: Record<string, string> = {
    acento: 'var(--acento-fraco)',
    recebido: 'var(--color-recebido-fraco)',
    atencao: 'var(--color-atencao-fraco)',
    vencido: 'var(--color-vencido-fraco)',
  };
  return (
    <div
      className="rounded-md border-l-[3px] px-4 py-3 text-sm"
      style={{
        borderColor: COR_DO_TOM[tom],
        background: fundo[tom],
        color: 'var(--texto)',
      }}
    >
      {children}
    </div>
  );
}
